/* eslint-disable max-lines */
import { BorderOutlined, CheckSquareFilled, EditOutlined, LinkOutlined, MinusSquareOutlined } from '@ant-design/icons'
import { getMiitelWidget, getOrganizationIdFromPath } from '@salescore/client-base'
import { SanitizedHtml } from '@salescore/client-common'
import {
  closeLongTextViewer,
  openLongTextViewer,
  selectCursor,
  selectLongTextViewerOpenKey,
  useAppDispatch,
  useAppSelector,
} from '@salescore/client-redux'
import {
  CORE_CONSTANT,
  type ViewQueryList,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithParent,
} from '@salescore/core'
import { logger, numberWithDelimiterFilter, useHover } from '@salescore/frontend-common'
import { App, Button, Popover, Select, Tag } from 'antd'
import dayjs from 'dayjs'
import { Array } from 'effect'
import { t } from 'i18next'
import { type CSSProperties, type ReactElement, type ReactNode, useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

import { useVmid } from '../../../components/VmidContext.tsx'
import type { RSheetsSelectOption, SheetColumn } from '../../../domain/column.ts'
import { useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { MINIMUM_WIDTH } from '../../../rsheet/components/body/const.tsx'
import { getActiveConditionalEffects } from '../../../rsheet/util/conditionalEffects.ts'
import { CellLongTextViewerTrigger } from './CellLongTextViewerTrigger.tsx'

export const SheetValueCellBody = ({
  column,
  height,
  recordNode,
  setCursorAndUseEditMode,
  style,
  value,
  rowIndex,
  columnIndex,
  innerRowIndex,
  query,
  ability,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  value: unknown
  recordNode: ViewQueryRecordNodeWithParent
  height: number
  style: {
    color?: string // 通常RSheetsCell側で定義されるが、SelectOptionCellなど一部でコンポーネント側で上書きが必要なため
    backgroundColor?: string // 同上
  }
  setCursorAndUseEditMode: () => void
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 手抜き
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: KPIだから不要なのでは？
  if (value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
    return (
      <StringCell
        value={`小計`}
        height={height}
        column={column}
        recordNode={recordNode}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndex}
      />
    )
  }
  if (value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING) {
    return (
      <StringCell
        value={'-'}
        height={height}
        column={column}
        recordNode={recordNode}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndex}
      />
    )
  }

  switch (column.metaType) {
    case 'record_url': {
      return <RecordUrlCell />
    }
    case 'relation': {
      return <ReferenceCell value={value} column={column} recordNode={recordNode} style={style} />
    }
    case 'id': {
      return <ReferenceCell value={value} column={column} recordNode={recordNode} style={style} />
    }
    case 'url': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'phone_number': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'email': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'html': {
      return <HtmlCell value={value} />
    }
    default: {
      break
    }
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 型設計が良くないからこんな条件で判断することになっている
  if ((column.selectOptions ?? []).length > 0) {
    if (column.metaType === 'multi_select') {
      return <MultiSelectOptionCell value={value} column={column} />
    }
    return <SelectOptionCell value={value} column={column} />
  }

  const { type } = column
  switch (type) {
    case 'integer' as const: {
      return <NumberCell value={value} />
    }
    case 'numeric' as const: {
      return <NumberCell value={value} />
    }
    case 'date' as const: {
      return <DateCell value={value} />
    }
    case 'datetime' as const: {
      return <DatetimeCell value={value} />
    }
    case 'time' as const: {
      return <TimeCell value={value} />
    }
    case 'boolean' as const: {
      return (
        <BooleanCell
          value={value}
          column={column}
          rowIndex={rowIndex}
          innerRowIndex={innerRowIndex}
          recordNode={recordNode}
          query={query}
          ability={ability}
        />
      )
    }
    case 'string' as const: {
      return (
        <StringCell
          value={value}
          column={column}
          recordNode={recordNode}
          height={height}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          innerRowIndex={innerRowIndex}
        />
      )
    }
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    case 'object' as const: {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO
      return (
        <StringCell
          value={value}
          column={column}
          recordNode={recordNode}
          height={height}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          innerRowIndex={innerRowIndex}
        />
      )
    }
    case 'array' as const: {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: どう表示させるか？一旦JSONにして表示している
      return (
        <MultiSelectOptionCell
          value={value}
          column={{
            ...column,
            selectOptions: Array.isArray(value)
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                value.map((value) => ({ value: value as string, label: value as string, color: 'default' }))
              : [],
          }}
        />
      )
    }
    case 'notFound' as const: {
      return <></>
    }
  }
}
const RecordUrlCell = (): ReactElement => (
  // リンクを開くのはsetMainCursorByRecordIdMutation側で行うため、ここではaタグは不要
  // <a href={value as string} target="_blank" style={{ color: '#aaa' }}>
  <div style={{ color: '#aaa' }} className="w-full cursor-pointer">
    <LinkOutlined />
  </div>
  // </a>
)

const getUrlProtocol = (column: SheetColumn<ViewQueryRecordNode>): string => {
  const oid = getOrganizationIdFromPath()
  const callToOrganizationIds: string[] = [`275afebc-0fef-4f0c-9101-e36aa7816678`]
  switch (column.metaType) {
    case 'phone_number': {
      return callToOrganizationIds.includes(oid) ? 'callto:' : 'tel:'
    }
    case 'email': {
      return 'mailto:'
    }
    default: {
      return ''
    } // http
  }
}
// url, tel, emailなどのリンク付きセル
const UrlCell = ({
  value,
  column,
  style: { color },
  setCursorAndUseEditMode,
}: {
  value: unknown
  column: SheetColumn<ViewQueryRecordNode>
  style: {
    color?: string
  }
  setCursorAndUseEditMode: () => void
}): ReactElement => {
  const element = useHover((hovered) => {
    const strings = z.string().array().safeParse(value)
    const string = z.string().safeParse(value)
    // eslint-disable-next-line sonarjs/no-nested-conditional
    const emails = strings.success ? strings.data : string.success ? [string.data] : undefined

    if (emails === undefined || emails.length === 0) {
      return <></>
    }

    return (
      <div style={{ width: '100%', height: '100%', padding: '0 2px' }}>
        {emails.map((email, index) => (
          <a
            key={`${email}_${index}`}
            href={`${getUrlProtocol(column)}${email}`}
            target={column.metaType !== undefined && ['url', 'email'].includes(column.metaType) ? '_blank' : ''}
            style={{ textDecoration: 'underline', color }}
            onPointerDown={(event) => {
              // RsheetsCellでstate更新をonPointerDownで行っており、それをe.stopPropagation()で止める必要がある。
              // onClickはクリックを離した時に発火するのに対して、onPointerDownはクリックを押した時に発火するため、
              // onClickだと間に合わないのでonPointerDownでもe.stopPropagation()をする。
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()

              // phoneのとき、miitelの判定を行う
              if (column.metaType === 'phone_number') {
                const miitel = getMiitelWidget()
                if (miitel !== undefined) {
                  miitel.call(email)
                  event.preventDefault()
                }
              }
            }}
            rel="noreferrer"
          >
            {email}
          </a>
        ))}
        <Button
          icon={<EditOutlined />}
          style={{
            position: 'absolute',
            top: 4,
            right: 2,
            opacity: hovered ? 100 : 0,
          }}
          onClick={(event) => {
            event.stopPropagation()
            setCursorAndUseEditMode()
          }}
        />
      </div>
    )
  })

  return <>{element}</>
}

const ReferenceCell = ({
  value,
  column,
  recordNode,
  style: { backgroundColor, color },
}: {
  value: unknown
  column: SheetColumn<ViewQueryRecordNode>
  recordNode: ViewQueryRecordNodeWithParent
  style: {
    color?: string
    backgroundColor?: string
  }
}): ReactElement => {
  const label = column.label === undefined ? undefined : column.label(recordNode)
  const colorStyle = {
    color,
    borderColor: color,
    backgroundColor,
  }

  if (
    value === undefined ||
    value === null ||
    !(typeof value === 'string' ? value !== '' : !Array.isArray(value) || value.length > 0)
  ) {
    return <span style={{ ...colorStyle, color: colorStyle.color ?? '#ccc' }}></span>
  }
  if (label === undefined || label === null) {
    return (
      <span style={{ ...colorStyle, color: colorStyle.color ?? '#ccc' }}>
        <>
          {t('該当なし')} id: {value}
        </>
      </span>
    )
  }

  return (
    <Tag
      className="bg-gray-50 text-gray-900"
      style={{
        fontSize: 14,
        padding: '3px 10px',
        marginTop: -4,
        ...colorStyle,
      }}
    >
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        label as string
      }
    </Tag>
  )
}
const NumberCell = ({ value }: { value: unknown }): ReactElement => {
  const text = numberWithDelimiterFilter(value)
  return (
    <div className={`w-full truncate text-right`}>
      <span>{text}</span>
    </div>
  )
}
const formatToDatetime = (x: string) => {
  try {
    // memo: 以前ここでタイムゾーンの考慮をしてしまっていたが、pg側をtimestamptzで統一したので、不要となった
    return t(`{{datetime, datetimestr}}`, { datetime: dayjs(x).toDate() })
  } catch {
    return x
  }
}
const DatetimeCell = ({ value }: { value: unknown }): ReactElement => {
  if (typeof value !== 'string') {
    return <></>
  }

  // 時刻まで表示したいことがなさそうなので、いったん日付のみを表示する
  // dayjsを使うとレンダリングの負荷があがるだろうか？要検討
  return <span>{formatToDatetime(value)}</span>
}
// デフォルトのチェックボックスを使うと、クリックイベントが扱いづらいため、純粋に表示のみを行うコンポーネントとして実装している
export const MyCheckbox = ({ value }: { value: boolean | null | undefined }): ReactElement => {
  if (value === undefined) {
    // 列追加直後の fetch 待ち。null と同じ表示にすると取得完了時にちらつくので skeleton にする
    return <span className="inline-block size-4 animate-pulse rounded bg-gray-200" />
  }
  if (value === true) {
    return <CheckSquareFilled style={{ fontSize: 16, color: '#0D6FDA' }} />
  }
  if (value === false) {
    return <BorderOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
  }
  return <MinusSquareOutlined style={{ fontSize: 16, color: '#d9d9d9' }} />
}
// custom viewDefinitionSQLなどでboolean列がtextのまま残る場合に文字列を正規化する。
// undefined（fetch 前）は保持し、空文字や想定外値は null（未入力）扱い。
const normalizeBooleanValue = (value: unknown): boolean | null | undefined => {
  if (value === undefined) {
    return undefined
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return null
}
/** type: 'boolean' or 'checkbox'のチェックボックスセル */
const BooleanCell = ({
  column,
  value,
  rowIndex,
  innerRowIndex,
  recordNode,
  query,
  ability,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  value: unknown
  rowIndex: number
  innerRowIndex: number
  recordNode: ViewQueryRecordNodeWithParent
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const { message } = App.useApp()

  const normalizedValue = normalizeBooleanValue(value)
  const checkbox = <MyCheckbox value={normalizedValue} />
  return (
    <div
      className="w-fit cursor-pointer"
      onClick={() => {
        const isColumnReadonly = column.readonly
        const isCellDisabled = getActiveConditionalEffects(column.conditionalEffects, recordNode, {}).some(
          (x) => x.effect === 'disable',
        )
        if (isColumnReadonly || isCellDisabled) {
          // "列" or "セル"の書き込み禁止なら、メッセージ表示して値の更新を行わない
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
          void message.warning(isColumnReadonly ? t('このカラムは書き込み禁止です') : t('このセルは書き込み禁止です'))
          return
        }

        // チェックボックス本体がクリックされたらトグルする
        const toggledValue = normalizedValue !== true
        upsertViewRecordsEntry({
          recordChanges: [
            {
              rowIndex,
              innerRowIndex,
              isNestedColumn: column.node.path.length > 1,
              nodePath: column.node.path,
              field: column.field,
              value: toggledValue,
              label: undefined,
            },
          ],
        })
      }}
    >
      {checkbox}
    </div>
  )
}
const SelectOptionTag = ({
  value,
  selectOptions,
}: {
  value: unknown
  selectOptions: RSheetsSelectOption[]
}): ReactElement => {
  if (value === undefined || value === '' || value === null || !['string', 'number'].includes(typeof value)) {
    return <></>
  }

  const option = selectOptions.find((option) => option.value === value)

  return (
    <Tag
      color={option?.color ?? 'default'}
      style={{
        // color: overrideColor, // ハイライト条件が設定されているとき、背景色は元のまま、文字色と枠線を変える -> 2023/06 いったん元の色を維持する仕様に変更
        // borderColor: overrideColor,
        fontSize: 14,
        padding: '3px 10px',
        marginTop: -4,
        opacity: option === undefined ? 0.7 : 1,
      }}
    >
      {/* 選択値以外の表示はしない仕様も検討したが、salesforceで「選択肢だが自由入力可」という入力項目があるため表示することにした */}
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        option?.label ?? (value as string)
      }
    </Tag>
  )
}
const SelectOptionCell = ({
  value,
  column: { selectOptions },
}: {
  value: unknown
  column: SheetColumn<ViewQueryRecordNode>
}): ReactElement => <SelectOptionTag value={value} selectOptions={selectOptions ?? []} />

const MultiSelectOptionCell = ({
  value,
  column,
}: {
  value: unknown
  column: SheetColumn<ViewQueryRecordNode>
}): ReactElement => {
  const values = Array.isArray(value) ? value : [value]
  const validated = z.union([z.string(), z.number()]).array().safeParse(values)

  const selectedOptions = validated.success
    ? validated.data
        // 2023/05 ELT側の不具合で、Salesforceの複数選択項目が空のとき、['']という1要素の配列になってしまう
        // ELT側で対応すべき処理だが、即時の対応が難しかったため、一旦こちらで対応
        // 同様の処理をMultiSelectOptionInputCellでもやっている
        // https://github.com/salescore-inc/issues/issues/198
        .filter((v) => (typeof v === 'string' ? v !== '' : true))
        .map(
          (v) =>
            column.selectOptions?.find((option) => option.value === v) ?? {
              // eslint-disable-next-line sonarjs/no-nested-conditional
              label: (typeof v === 'string' ? v !== '' : true) ? v : `(空文字列)`, // 上記でフィルタしているので不要なはずだが一応
              color: 'gray',
            },
        )
    : []

  if (value === undefined || value === '') {
    return <></>
  }

  if (!validated.success) {
    return <></>
  }

  return (
    <Select
      mode="multiple"
      style={{ width: '100%', marginTop: -6 }}
      maxTagCount="responsive"
      value={selectedOptions.map((option) => `${option.label}`)}
      open={false}
      removeIcon={null}
      suffixIcon={null}
      variant="borderless"
      tagRender={({ value: tagValue, isMaxTag, label }) => {
        if (isMaxTag && typeof label === 'string') {
          return (
            <Tag
              style={{
                fontSize: 14,
                padding: '3px 10px',
                marginInlineEnd: 0,
              }}
            >
              {label.slice(0, -3)}
            </Tag>
          )
        }
        const option = selectedOptions.find((o) => `${o.label}` === tagValue)

        return (
          <Tag
            color={option?.color ?? 'gray'}
            style={{
              fontSize: 14,
              padding: '3px 10px',
              marginInlineEnd: 2,
            }}
          >
            {option?.label ?? tagValue}
          </Tag>
        )
      }}
    />
  )
}
const TimeCell = ({ value }: { value: unknown }): ReactElement => {
  if (value === undefined || value === null) {
    return <></>
  }

  if (typeof value !== 'string') {
    logger.debug(`TimeCell expected string type. value:`, value)
    return <></>
  }
  if (/^\d{2}:\d{2}/v.test(value)) {
    return <pre style={{ overflow: 'hidden' }}>{value.slice(0, 5)}</pre>
  }

  return <pre style={{ overflow: 'hidden' }}>{value}</pre>
}
const DateCell = ({ value }: { value: unknown }): ReactElement => (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  <pre style={{ overflow: 'hidden' }}>{value as string}</pre>
)
const StringCell = ({
  value,
  height,
  column,
  recordNode,
  rowIndex,
  columnIndex,
  innerRowIndex,
}: {
  value: unknown
  height: number
  column: SheetColumn<ViewQueryRecordNode>
  recordNode: ViewQueryRecordNodeWithParent
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
}): ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const label = column.label === undefined ? undefined : (column.label(recordNode) as string) // 2023/08 StringCellだがラベル値があるのは、現状はsalescore_users_with_groupのみ
  const textLineSize = Math.floor(height * 1.82)
  const lineClampStyle: CSSProperties = {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: textLineSize,
    display: '-webkit-box',
  }
  const stringValue =
    typeof value === 'string'
      ? value
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        typeof value === 'number'
        ? `${value}`
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          value === undefined || value === null
          ? ``
          : JSON.stringify(value)
  const text = label ?? stringValue
  // 1行のときはspanで親要素divのtextOverflowによって三点リーダを表示し、1行より大きい場合はwebkit-line-clampを使って表示
  const inner = height === 1 ? <span>{text}</span> : <p style={lineClampStyle}>{text}</p>
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: ここでpopoverしてしまうとpadding分を考慮できずhoverの判定が微妙になる。構造を変える必要がある。
  // ロングテキスト列以外は overflow 計測を回さない（全セルで useCellOverflowOnHover を走らせると重い）
  if (column.metaType !== 'text') {
    return (
      <Popover content={<pre style={{ width: 400 }}>{text}</pre>} placement="bottomLeft">
        {inner}
      </Popover>
    )
  }
  return (
    <LongTextStringCell
      text={text}
      column={column}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndex}
    >
      {inner}
    </LongTextStringCell>
  )
}

/**
 * ロングテキスト（metaType === 'text'）専用の StringCell。
 * cursor 位置 / 長文 viewer の open 状態を subscribe する。
 *
 * subscribe を別コンポーネントに切り出した理由:
 * StringCell は string / object / KPI 小計セルなど多数のセルで render されるため、
 * useAppSelector を StringCell 直下で呼ぶと subscribe 数が爆発する（過去の cursorAtom 問題と同種）。
 * metaType === 'text' のセル（=長文列のみ）に subscribe を限定するため、別コンポーネントへ切り出している。
 *
 * 長文 viewer の open 状態は Redux（viewsSlice.ViewState.longTextViewerOpenKey）で view ごとに 1 つだけ保持し、
 * 同一 view 内で別セルを open した瞬間に前のセルは自動的に close される（同一 view 内の mutual exclusion）。
 * 複数 view が同時マウントされる場合（drill-down modal / split-pane 等）はそれぞれ独立に open できる。
 */
const LongTextStringCell = ({
  text,
  column,
  rowIndex,
  columnIndex,
  innerRowIndex,
  children,
}: {
  text: string
  column: SheetColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  children: ReactNode
}): ReactElement => {
  const vmid = useVmid()
  const dispatch = useAppDispatch()
  const cellKey = `${rowIndex}-${columnIndex}-${innerRowIndex}`
  const isActive = useAppSelector((state) => {
    const cursor = selectCursor(state, vmid)
    return (
      cursor?.main.rowIndex === rowIndex &&
      cursor.main.columnIndex === columnIndex &&
      cursor.main.innerRowIndex === innerRowIndex
    )
  })
  const viewerOpen = useAppSelector((state) => selectLongTextViewerOpenKey(state, vmid) === cellKey)
  // unmount 時 / 仮想化スクロールアウト時に自分が open ならクリア。
  // 別セル open による自分の close は state が他セル key になっているため no-op になる（if-matches セマンティクス）。
  useEffect(() => {
    if (!viewerOpen) {
      return
    }
    return (): void => {
      dispatch(closeLongTextViewer({ vmid, cellKey }))
    }
  }, [viewerOpen, cellKey, vmid, dispatch])
  const handleViewerOpenChange = useCallback(
    (next: boolean): void => {
      if (next) {
        dispatch(openLongTextViewer({ vmid, cellKey }))
      } else {
        dispatch(closeLongTextViewer({ vmid, cellKey }))
      }
    },
    [dispatch, vmid, cellKey],
  )
  const [hoverOpen, setHoverOpen] = useState(false)
  return (
    <CellLongTextViewerTrigger
      text={text}
      columnWidth={column.width}
      open={viewerOpen}
      onOpenChange={handleViewerOpenChange}
      forceVisible={isActive}
    >
      <Popover
        content={<pre style={{ width: 400 }}>{text}</pre>}
        placement="bottomLeft"
        open={hoverOpen && !viewerOpen}
        onOpenChange={setHoverOpen}
      >
        {children}
      </Popover>
    </CellLongTextViewerTrigger>
  )
}
const HtmlCell = ({ value }: { value: unknown }): ReactElement => {
  if (typeof value !== 'string') {
    return <></>
  }
  const htmlContent = <SanitizedHtml rawHtmlText={value} />

  return (
    <Popover
      content={
        // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- html-cell-popover は main.less にスタイル定義あり
        <div className="html-cell-popover" style={{ width: 600, maxHeight: '60vh', overflow: 'scroll' }}>
          {htmlContent}
        </div>
      }
      placement="rightTop"
    >
      <div>{htmlContent}</div>
    </Popover>
  )
}
